import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Dashboard() {
  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const now = Timestamp.now();
      const thirtyDaysAgo = Timestamp.fromMillis(now.toMillis() - 30 * 24 * 60 * 60 * 1000);

      const [bookings, listings, recentBookings] = await Promise.all([
        getDocs(query(collection(db, 'bookings'), where('status', '==', 'confirmed'))),
        getDocs(query(collection(db, 'listings'), where('status', '==', 'published'))),
        getDocs(
          query(
            collection(db, 'bookings'),
            where('createdAt', '>=', thirtyDaysAgo),
            where('status', '==', 'confirmed')
          )
        ),
      ]);

      const totalRevenue = bookings.docs.reduce((sum, doc) => {
        const data = doc.data();
        return sum + (data.totalCents || 0);
      }, 0);

      const monthlyRevenue = recentBookings.docs.reduce((sum, doc) => {
        const data = doc.data();
        return sum + (data.totalCents || 0);
      }, 0);

      return {
        totalBookings: bookings.size,
        activeListings: listings.size,
        totalRevenue: totalRevenue / 100,
        monthlyRevenue: monthlyRevenue / 100,
        pendingBookings: 0, // Calculate from bookings with status 'pending'
      };
    },
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-600 text-sm font-medium mb-2">Total Bookings</h3>
            <p className="text-3xl font-bold">{stats.totalBookings}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-600 text-sm font-medium mb-2">Active Listings</h3>
            <p className="text-3xl font-bold">{stats.activeListings}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-600 text-sm font-medium mb-2">Total Revenue</h3>
            <p className="text-3xl font-bold">R {stats.totalRevenue.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-600 text-sm font-medium mb-2">Monthly Revenue</h3>
            <p className="text-3xl font-bold">R {stats.monthlyRevenue.toFixed(2)}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Revenue Overview</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={[]}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="revenue" fill="#0ea5e9" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

